import _ from 'lodash';
import axios from 'axios';
import { APP_NAMESPACE, SUCCESS } from '../../util/redux-constants';
import { get, post } from '../../util/http-utils';
import { deleteCookie, getCookie, setCookie } from '../../util/cookie-utils';
import { updateStore, buildGenericInitialState, handleError } from '../../util/store-utils';
import { getAppUrl, getApiUrl } from '../../util/environment-utils';

const AUTH_ENDPOINT_BASE = 'auth';
const typeBase = `${APP_NAMESPACE}/${AUTH_ENDPOINT_BASE}/`;

// Constants
export const CHANGE_AUTH = `${typeBase}CHANGE_AUTH`;
export const SET_POST_AUTH_PATH = `${typeBase}SET_POST_AUTH_PATH`;
export const RESET_PASSWORD = `${typeBase}RESET_PASSWORD`;
export const GET_AUTHENTICATED_USER = `${typeBase}GET_AUTHENTICATED_USER`;

// Actions
export const changeAuthentication = payload => dispatch =>
  dispatch({
    type: CHANGE_AUTH,
    payload,
  });

/**
 * login - Authenticate a user with an email and password
 * @param {Object} credentials  Login credentials (email, password)
 */
export const login = (credentials, desiredPath) => async (dispatch) => {
  try {
    console.log('[FRONTEND] Login attempt with credentials:', {
      email: credentials.email,
      password: credentials.password ? '***' : 'missing'
    });

    let authResponse = null;

    try {
      const res = await axios.post(`${getApiUrl()}/auth/login`, {
        email: credentials.email,
        password: credentials.password,
      });
      authResponse = res.data;
    } catch (apiErr) {
      console.warn('[FRONTEND] API Login error:', apiErr);
      const errMsg = apiErr.response?.data?.errors?.[0]?.error || apiErr.message;
      throw new Error(errMsg);
    }

    if (!authResponse || !authResponse.token) {
      throw new Error('Inloggningen misslyckades.');
    }

    console.log('[FRONTEND] Login successful! Role:', authResponse.user?.role);
    setCookie('token', authResponse.token, { maxAge: authResponse.tokenExpiration || 86400 * 30 });
    localStorage.setItem('userProfile', JSON.stringify(authResponse.user));

    dispatch({
      type: CHANGE_AUTH,
      payload: authResponse
    });

    if (desiredPath) {
      window.location.href = `${getAppUrl()}${desiredPath}`;
    } else {
      window.location.href = `${getAppUrl()}/dashboard`;
    }

  } catch (err) {
    console.error('[FRONTEND] Login error:', err);
    await handleError(dispatch, err, CHANGE_AUTH);
  }
};

/**
 * register - Creates a new account for a user
 * @param {Object} formData  User's form data
 */
export const register = formData => async (dispatch) => {
  try {
    const payload = {
      email: formData.email,
      password: formData.password,
      name: {
        first: formData.firstName || 'Användare',
        last: formData.lastName || '',
      },
      role: formData.role || 'participant',
    };

    let authResponse = null;
    try {
      const res = await axios.post(`${getApiUrl()}/auth/register`, payload);
      authResponse = res.data;
    } catch (apiErr) {
      console.warn('[FRONTEND] API Register error:', apiErr);
      const errMsg = apiErr.response?.data?.errors?.[0]?.error || apiErr.message;
      throw new Error(errMsg);
    }

    if (!authResponse || !authResponse.token) {
      throw new Error('Registreringen misslyckades.');
    }

    console.log('[FRONTEND] Register successful! User:', authResponse.user);
    setCookie('token', authResponse.token, { maxAge: authResponse.tokenExpiration || 86400 * 30 });
    localStorage.setItem('userProfile', JSON.stringify(authResponse.user));

    dispatch({
      type: CHANGE_AUTH,
      payload: authResponse
    });

    window.location.href = `${getAppUrl()}/dashboard`;

  } catch (err) {
    console.error('[FRONTEND] Register error:', err);
    await handleError(dispatch, err, CHANGE_AUTH);
  }
};

/**
 * setPostAuthPath  - Save Desired Pre-Auth Path to State
 * @param {String} payload  The desired path, saved pre-authentication
 * @returns {function}
 */
export const setPostAuthPath = payload => dispatch =>
  dispatch({
    type: SET_POST_AUTH_PATH,
    payload,
  });

/**
 * logoutUser  - Log user out by clearing auth state and token cookie
 */

export const logoutUser = () => (dispatch) => {
  dispatch({ type: CHANGE_AUTH, payload: {} });
  deleteCookie('token');
  localStorage.removeItem('userProfile');

  window.location.href = `${getAppUrl()}/login`;
};

/**
 * forgotPassword - Sends user an email with a token to reset their password
 * @param {Object} formData  The user's email address
 * @returns {Promise}
 */
export const forgotPassword = formData => async (dispatch) => {
  try {
    // MOCK FORGOT PASSWORD IMPLEMENTATION
    await new Promise(resolve => setTimeout(resolve, 1000));

    const mockUsers = JSON.parse(localStorage.getItem('mock_users') || '[]');
    const userIndex = mockUsers.findIndex(u => u.email.toLowerCase() === formData.email.toLowerCase());

    if (userIndex === -1) {
      throw new Error('E-postadressen finns inte registrerad.');
    }

    // CHECK 1: Ensure user is resetting the correct role type
    const targetUser = mockUsers[userIndex];
    if (formData.role === 'teacher') {
      if (targetUser.role !== 'teacher' && targetUser.role !== 'admin') {
        // mismtach
      }
      if (!formData.teacherCode) {
        throw new Error('Du måste skriva in koden, det är obligatoriskt för lärare.');
      }
      if (formData.teacherCode !== 'Healthweek@nti') {
        throw new Error('Fel lärarkod. Kontakta administratör om du glömt koden.');
      }
    } else {
      if (targetUser.role === 'teacher' || targetUser.role === 'admin') {
        throw new Error('Detta är ett lärarkonto. Vänligen välj "Lärare" och ange koden.');
      }
    }

    // Update password
    if (!formData.password) {
      throw new Error('Du måste ange ett nytt lösenord.');
    }

    mockUsers[userIndex].password = formData.password;
    localStorage.setItem('mock_users', JSON.stringify(mockUsers));

    const successMessage = 'Ditt lösenord har återställts! Du kan nu logga in.';

    dispatch({
      type: RESET_PASSWORD,
      payload: { message: successMessage },
      meta: { status: SUCCESS }
    });

    return Promise.resolve({ message: successMessage });
  } catch (err) {
    await handleError(dispatch, err, RESET_PASSWORD);
  }
};

/**
 * resetPassword - Resets a user's password, given a valid token
 * @param {Object} formData  The user's email address
 * @param {String} token     Valid token required for password reset
 * @returns {Promise}
 */
export const resetPassword = (formData, token) => async (dispatch) => {
  try {
    const response = await post(dispatch, CHANGE_AUTH, `${AUTH_ENDPOINT_BASE}/reset-password/${token}`, formData, false);
    return Promise.resolve(response);
  } catch (err) {
    await handleError(dispatch, err, CHANGE_AUTH);
  }
};

/**
 * fetchAuthenticatedUser - Retrieves the logged in user's information
 * @returns {Promise}
 */
export const fetchAuthenticatedUser = () => async (dispatch) => {
  try {
    const token = getCookie('token');
    if (token) {
      try {
        const res = await axios.get(`${getApiUrl()}/auth/profile`, {
          headers: { Authorization: token.startsWith('JWT ') ? token : `JWT ${token}` },
        });
        if (res.data && res.data.user) {
          localStorage.setItem('userProfile', JSON.stringify(res.data.user));
          dispatch({
            type: GET_AUTHENTICATED_USER,
            payload: res.data,
          });
          return Promise.resolve(res.data);
        }
      } catch (err) {
        console.warn('Could not fetch remote profile, fallback to stored:', err.message);
      }
    }

    const storedProfile = localStorage.getItem('userProfile');
    const user = storedProfile ? JSON.parse(storedProfile) : null;
    const response = { user };

    dispatch({
      type: GET_AUTHENTICATED_USER,
      payload: response,
    });

    return Promise.resolve(response);
  } catch (err) {
    await handleError(dispatch, err, GET_AUTHENTICATED_USER);
    return Promise.reject(err);
  }
};

// Store
const INITIAL_STATE = {
  authenticated: Boolean(getCookie('token')),
  user: null,
  profile: JSON.parse(localStorage.getItem('userProfile')) || null,
  ...buildGenericInitialState([CHANGE_AUTH, SET_POST_AUTH_PATH, RESET_PASSWORD, GET_AUTHENTICATED_USER]),
};

export default (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case CHANGE_AUTH:
      const profile = _.get(action, 'payload.user');
      if (profile) {
        localStorage.setItem('userProfile', JSON.stringify(profile));
      }
      return updateStore(state, action, {
        authenticated: Boolean(_.get(action, 'payload.token')),
        user: _.get(action, 'payload.user.id'),
        profile
      });
    case RESET_PASSWORD:
      return updateStore(state, action);
    case GET_AUTHENTICATED_USER:
      const userProfile = _.get(action, 'payload.user');
      if (userProfile) {
        localStorage.setItem('userProfile', JSON.stringify(userProfile));
      }
      return updateStore(state, action, {
        user: _.get(action, 'payload.user.id'),
        profile: userProfile
      });
    default:
      return state;
  }
};
